import React, { useState, useEffect } from "react";
import {
  Download,
  Filter,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader,
  X,
  Eye,
  Settings,
} from "lucide-react";
import { adminReportService } from "@/services/api/admin.report.service";

const ContactExport = ({ isOpen, onClose, currentFilters = {} }) => {
  const [exportForm, setExportForm] = useState({
    format: "xlsx",
    status: "",
    startDate: "",
    endDate: "",
    limit: 1000,
    includeFields: [],
  });

  const [availableFields, setAvailableFields] = useState([]); // Initialize as empty array
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState("");
  const [exportSuccess, setExportSuccess] = useState(false);

  // Status options matching your existing component
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "new", label: "New" },
    { value: "in-progress", label: "In Progress" },
    { value: "replied", label: "Replied" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
    { value: "spam", label: "Spam" },
  ];

  // Initialize form with current filters from main component
  useEffect(() => {
    if (currentFilters && isOpen) {
      setExportForm((prev) => ({
        ...prev,
        status: currentFilters.status || "",
        // Set default date range (last 30 days)
        startDate:
          currentFilters.startDate ||
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
        endDate:
          currentFilters.endDate || new Date().toISOString().split("T")[0],
      }));
    }
  }, [currentFilters, isOpen]);

  // Fetch available fields on component mount
  useEffect(() => {
    if (isOpen) {
      fetchAvailableFields();
    }
  }, [isOpen]);

  const fetchAvailableFields = async () => {
    try {
      const response = await adminReportService.getContactExportFields();
      if (response.success && Array.isArray(response.data)) {
        setAvailableFields(response.data);
        // Select all fields by default
        setExportForm((prev) => ({
          ...prev,
          includeFields: response.data.map((field) => field.key),
        }));
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Failed to fetch export fields:", error);
      // Fallback to default fields if API fails
      const defaultFields = [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "mobile", label: "Mobile" },
        { key: "subject", label: "Subject" },
        { key: "message", label: "Message" },
        { key: "status", label: "Status" },
        { key: "createdAt", label: "Created At" },
        { key: "adminNotes", label: "Admin Notes" },
        { key: "assignedTo", label: "Assigned To" },
      ];
      setAvailableFields(defaultFields);
      setExportForm((prev) => ({
        ...prev,
        includeFields: defaultFields.map((field) => field.key),
      }));
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name === "includeFields") {
        setExportForm((prev) => ({
          ...prev,
          includeFields: checked
            ? [...prev.includeFields, value]
            : prev.includeFields.filter((field) => field !== value),
        }));
      }
    } else {
      setExportForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear errors when user makes changes
    setError("");
  };

  const validateForm = () => {
    const validation = adminReportService.validateExportParams(exportForm);

    if (!validation.isValid) {
      setError(validation.errors.join(", "));
      return false;
    }

    if (exportForm.includeFields.length === 0) {
      setError("Please select at least one field to export");
      return false;
    }

    return true;
  };

  const handlePreview = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError("");

    try {
      const params = adminReportService.buildExportParams(exportForm);
      params.fields = exportForm.includeFields.join(",");

      const response = await adminReportService.previewContactExport(params);

      if (response.success) {
        setPreviewData(response.data);
        setShowPreview(true);
      } else {
        setError(response.message || "Failed to generate preview");
      }
    } catch (error) {
      setError(error.message || "Failed to generate preview");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (!validateForm()) return;

    setIsExporting(true);
    setError("");

    try {
      const params = adminReportService.buildExportParams(exportForm);
      params.fields = exportForm.includeFields.join(",");

      const filename = `contacts_export_${
        new Date().toISOString().split("T")[0]
      }`;

      await adminReportService.downloadContactsExport(params, filename);

      setExportSuccess(true);
      setTimeout(() => {
        setExportSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      setError(error.message || "Failed to export contacts");
    } finally {
      setIsExporting(false);
    }
  };

  const resetForm = () => {
    setExportForm({
      format: "xlsx",
      status: "",
      startDate: "",
      endDate: "",
      limit: 1000,
      includeFields: Array.isArray(availableFields)
        ? availableFields.map((field) => field.key)
        : [],
    });
    setPreviewData(null);
    setShowPreview(false);
    setError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto z-50">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <Download className="mr-2" size={20} />
                Export Contacts
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                <div className="flex items-center">
                  <AlertCircle size={16} className="mr-2" />
                  {error}
                </div>
              </div>
            )}

            {exportSuccess && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                <div className="flex items-center">
                  <CheckCircle size={16} className="mr-2" />
                  Export completed successfully!
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Export Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <select
                  name="format"
                  value={exportForm.format}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="csv">CSV (.csv)</option>
                </select>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status Filter
                  </label>
                  <select
                    name="status"
                    value={exportForm.status}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Records
                  </label>
                  <select
                    name="limit"
                    value={exportForm.limit}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={100}>100 records</option>
                    <option value={500}>500 records</option>
                    <option value={1000}>1,000 records</option>
                    <option value={5000}>5,000 records</option>
                    <option value={10000}>All records</option>
                  </select>
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={exportForm.startDate}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={exportForm.endDate}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Field Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fields to Export
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                  {availableFields && availableFields.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {availableFields.map((field) => (
                        <label key={field.key} className="flex items-center">
                          <input
                            type="checkbox"
                            name="includeFields"
                            value={field.key}
                            checked={exportForm.includeFields.includes(
                              field.key
                            )}
                            onChange={handleFormChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">
                            {field.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Loading available fields...
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Section */}
              {showPreview && previewData && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Preview (First 5 records)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          {previewData.headers?.map((header) => (
                            <th key={header} className="px-2 py-1 text-left">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows?.slice(0, 5).map((row, index) => (
                          <tr key={index} className="border-t">
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-2 py-1">
                                {String(cell || "").substring(0, 50)}
                                {String(cell || "").length > 50 ? "..." : ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Total records to export: {previewData.totalCount || 0}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {isExporting ? (
                <>
                  <Loader className="animate-spin mr-2" size={16} />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2" size={16} />
                  Export
                </>
              )}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <Settings className="mr-2" size={16} />
              Reset
            </button>

            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactExport;
